/* =====================================================================
   Couche « award » — moteur d'animation de l'accueil, Annette D.
   GSAP 3.13 (ScrollTrigger, SplitText) et Lenis 1.1.20, chargés en différé
   par snippets/annetted-design.liquid. Direction validée par Léo le
   11 oct. 2026 d'après le prototype https://claude.ai/artifact/3zgZj82gbLiHT67ycrN3xf

   - Sur la collection, la fiche et l'À propos (chargée sur ordinateur seulement, voir
     snippets/annetted-design.liquid) : défilement fluide, curseur, photos qui glissent
     dans leur cadre, ouverture et citation de l'À propos (fonction `pages`).
   - Une scène par valeur de `data-award` (ouverture, manifeste, gestes,
     vitrine, essai, duo, atelier, journal), plus les titres
     `data-reveler="lignes"`, le curseur et les boutons aimantés.
   - Tout vit dans un gsap.matchMedia : au passage de 990 px ou d'un changement
     de préférence d'animation, tout est défait puis reconstruit.
   - Horizon fait défiler `.page-wrapper` et non le document à partir de
     990 px (le tiroir du panier « pousse » la page) : Lenis et ScrollTrigger
     visent ce conteneur sur ordinateur, le document en dessous.
   - « Réduire les animations », éditeur de thème : ni rideau, ni défilement
     fluide, ni épinglage ; le contenu est visible tel quel. Les états de départ
     ne sont posés que par ce script.
   ===================================================================== */

(() => {
  const doc = document.documentElement;
  const editeur = () => Boolean(window.Shopify && window.Shopify.designMode) || doc.classList.contains('shopify-design-mode');

  const finirRideau = () => {
    doc.classList.remove('annetted-rideau-actif');
    try {
      sessionStorage.setItem('annetted-rideau', '1');
    } catch (e) {
      /* stockage refusé : le rideau reviendra à la prochaine visite, sans gravité */
    }
  };

  // document.fonts.ready peut se résoudre avant que la feuille de polices soit lue : on attend
  // Bodoni elle-même, sinon les lignes sont coupées avec la police de secours (plus étroite).
  const policesPretes = () =>
    Promise.race([
      Promise.all([document.fonts.load('400 48px "Bodoni Moda"'), document.fonts.load('italic 400 48px "Bodoni Moda"')]),
      new Promise((r) => setTimeout(r, 2500)),
    ]);

  const SCENES = {};

  /* ------------------------------------------------------------ ouverture */
  SCENES.ouverture = (el, c) => {
    if (!c.mouvement || c.editeur) return null;
    const lignes = el.querySelectorAll('.annetted-ouverture__piste');
    const plateau = el.querySelector('.annetted-ouverture__plateau');
    const entrees = el.querySelectorAll('[data-entree]');
    // Pas d'opacité nulle sur la photo : c'est l'image du LCP, elle doit être peinte tout de suite.
    gsap.set(lignes, { yPercent: 118 });
    gsap.set(plateau, { scale: 1.12 });
    gsap.set(entrees, { y: 24, autoAlpha: 0 });
    c.intros.push(() =>
      gsap
        .timeline({ defaults: { ease: 'expo.out' } })
        .to(plateau, { scale: 1, duration: 2.4 }, 0)
        .to(lignes, { yPercent: 0, duration: 1.5, stagger: 0.12 }, 0.1)
        .to(entrees, { y: 0, autoAlpha: 1, duration: 1.1, stagger: 0.08 }, 0.7)
    );

    let retirer = null;
    if (c.pointeurFin && plateau) {
      const rx = gsap.quickTo(plateau, 'rotationX', { duration: 1.2, ease: 'power3.out' });
      const ry = gsap.quickTo(plateau, 'rotationY', { duration: 1.2, ease: 'power3.out' });
      const suivre = (e) => {
        ry((e.clientX / innerWidth - 0.5) * 9);
        rx((e.clientY / innerHeight - 0.5) * -7);
      };
      el.addEventListener('pointermove', suivre);
      retirer = () => el.removeEventListener('pointermove', suivre);
    }

    if (c.bureau) {
      gsap
        .timeline({ scrollTrigger: { trigger: el, start: 'top top', end: '+=45%', scrub: true, pin: true, refreshPriority: 2 } })
        .to(el.querySelector('.annetted-ouverture__plateau img'), { scale: 1.38, yPercent: -4, ease: 'none' }, 0)
        .to(el.querySelector('.annetted-ouverture__ligne--1'), { xPercent: -8, ease: 'none' }, 0)
        .to(el.querySelector('.annetted-ouverture__ligne--2'), { xPercent: 4, ease: 'none' }, 0)
        .to(el.querySelector('.annetted-ouverture__ligne--3'), { xPercent: -5, ease: 'none' }, 0)
        .to(el.querySelector('.annetted-ouverture__bas'), { autoAlpha: 0, y: -40, filter: 'blur(6px)', ease: 'none', duration: 0.45 }, 0)
        .to(el.querySelector('.annetted-ouverture__titre'), { autoAlpha: 0, ease: 'none', duration: 0.4 }, 0.6);
    }
    return retirer;
  };

  /* ------------------------------------------------------------ manifeste */
  SCENES.manifeste = (el, c) => {
    if (!c.mouvement || c.editeur) return null;
    const texte = el.querySelector('.annetted-manifeste__texte');
    c.apresPolices(() => {
      const mots = SplitText.create(texte, { type: 'words', wordsClass: 'annetted-mot' });
      gsap.fromTo(
        mots.words,
        { opacity: 0.3 },
        { opacity: 1, ease: 'none', stagger: 0.4, scrollTrigger: { trigger: el, start: 'top 72%', end: 'bottom 62%', scrub: true } }
      );
      gsap.from(el.querySelectorAll('.annetted-pastille'), {
        scale: 0.3,
        autoAlpha: 0,
        duration: 1,
        ease: 'expo.out',
        stagger: 0.15,
        scrollTrigger: { trigger: el, start: 'top 70%', once: true },
      });
    });
    return null;
  };

  /* ------------------------------------------------------------ gestes */
  SCENES.gestes = (el, c) => {
    const etapes = Array.from(el.querySelectorAll('.annetted-gestes__etape'));
    const images = Array.from(el.querySelectorAll('.annetted-gestes__images img'));
    const fig = el.querySelector('.annetted-gestes__fig');
    const compteur = el.querySelector('.annetted-gestes__compteur');
    if (!etapes.length) return null;
    const total = String(etapes.length).padStart(2, '0');
    let actuelle = 0;
    el.classList.add('annetted-gestes--pilote');
    etapes[0].classList.add('annetted-gestes__etape--active');
    const activer = (i) => {
      if (i === actuelle) return;
      etapes.forEach((e, n) => e.classList.toggle('annetted-gestes__etape--active', n === i));
      images.forEach((img, n) => {
        img.style.zIndex = n === i ? 2 : n === actuelle ? 1 : 0;
      });
      if (images[i]) {
        if (c.mouvement && !c.editeur) {
          gsap.fromTo(
            images[i],
            { clipPath: i > actuelle ? 'inset(100% 0% 0% 0%)' : 'inset(0% 0% 100% 0%)', scale: 1.12 },
            { clipPath: 'inset(0% 0% 0% 0%)', scale: 1, duration: 1.1, ease: 'expo.out', overwrite: true }
          );
        } else {
          gsap.set(images[i], { clipPath: 'inset(0% 0% 0% 0%)' });
        }
      }
      if (fig) fig.textContent = etapes[i].dataset.fig || '';
      if (compteur) compteur.textContent = `${String(i + 1).padStart(2, '0')} / ${total}`;
      actuelle = i;
    };
    etapes.forEach((etape, i) => {
      ScrollTrigger.create({ trigger: etape, start: 'top 55%', end: 'bottom 55%', onToggle: (self) => self.isActive && activer(i) });
    });
    return () => {
      el.classList.remove('annetted-gestes--pilote');
      etapes.forEach((e) => e.classList.remove('annetted-gestes__etape--active'));
      images.forEach((img) => {
        img.style.zIndex = '';
      });
    };
  };

  /* ------------------------------------------------------------ vitrine horizontale */
  SCENES.vitrine = (el, c) => {
    if (!c.bureau || !c.mouvement || c.editeur) return null;
    const fenetre = el.querySelector('.annetted-vitrine__fenetre');
    const piste = el.querySelector('.annetted-vitrine__piste');
    const barre = el.querySelector('.annetted-vitrine__barre i');
    if (!fenetre || !piste) return null;
    el.classList.add('annetted-vitrine--epinglee');
    const cartes = piste.querySelectorAll('.annetted-vitrine__carte');
    const marge = () => parseFloat(getComputedStyle(fenetre).paddingLeft) || 0;
    const distance = () => Math.max(0, piste.scrollWidth - fenetre.clientWidth + marge() * 2);
    // La page descend moins que la piste n'avance : la vitrine se traverse en un écran et demi.
    const course = () => Math.round(distance() * 0.6);
    // Sur un écran bas, titre et cartes dépassent la fenêtre : épinglée par le haut, la vitrine
    // coupait le bas des cartes (vu à 1250 × 612 le 14 oct.). On l'épingle un peu plus haut, juste
    // assez pour montrer la carte la plus haute en entier ; le titre passe alors sous l'en-tête.
    const hauteur = () => (c.scroller === window ? window.innerHeight : c.scroller.clientHeight);
    const decalage = () => {
      const haut = el.getBoundingClientRect().top;
      const bas = Math.max(...Array.from(cartes, (k) => k.getBoundingClientRect().bottom)) - haut;
      return Math.max(0, Math.ceil(bas + 24 - hauteur()));
    };
    const depart = () => `top+=${decalage()} top`;
    const horizontal = gsap.to(piste, {
      x: () => -distance(),
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: depart,
        end: () => `+=${course()}`,
        pin: true,
        scrub: 0.8,
        refreshPriority: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => barre && gsap.set(barre, { scaleX: self.progress }),
      },
    });
    piste.querySelectorAll('.annetted-vitrine__image img').forEach((img) => {
      gsap.fromTo(
        img,
        { xPercent: -7 },
        {
          xPercent: 7,
          ease: 'none',
          scrollTrigger: { trigger: img.parentElement, containerAnimation: horizontal, start: 'left right', end: 'right left', scrub: true },
        }
      );
    });
    const pencher = gsap.quickTo(cartes, 'skewX', { duration: 0.6, ease: 'power3.out' });
    ScrollTrigger.create({
      trigger: el,
      start: depart,
      end: () => `+=${course()}`,
      onUpdate: (self) => pencher(gsap.utils.clamp(-5, 5, self.getVelocity() / -260)),
      onLeave: () => pencher(0),
      onLeaveBack: () => pencher(0),
    });
    return () => el.classList.remove('annetted-vitrine--epinglee');
  };

  /* ------------------------------------------------------------ essai de gravure */
  SCENES.essai = (el, c) => {
    const champNoms = el.querySelector('[data-essai="noms"]');
    const champDate = el.querySelector('[data-essai="date"]');
    const sortieNoms = el.querySelector('.annetted-essai__noms');
    const sortieDate = el.querySelector('.annetted-essai__date');
    const gravure = el.querySelector('.annetted-essai__gravure');
    const laser = el.querySelector('.annetted-essai__laser');
    const lien = el.querySelector('[data-essai="lien"]');
    const plateau = el.querySelector('.annetted-essai__plateau');
    if (!champNoms || !sortieNoms) return null;

    // Le lien de commande emporte la saisie : la fiche remplit ses champs de personnalisation
    // (blocks/personnalisation.liquid lit les paramètres « perso:<libellé> »).
    const recopier = () => {
      sortieNoms.textContent = champNoms.value.trim() || champNoms.placeholder || '';
      if (champDate && sortieDate) sortieDate.textContent = champDate.value.trim() || champDate.placeholder || '';
      if (lien && lien.dataset.base) {
        const url = new URL(lien.dataset.base, location.origin);
        if (lien.dataset.champNoms && champNoms.value.trim()) url.searchParams.set(`perso:${lien.dataset.champNoms}`, champNoms.value.trim());
        if (champDate && lien.dataset.champDate && champDate.value.trim()) url.searchParams.set(`perso:${lien.dataset.champDate}`, champDate.value.trim());
        lien.href = url.pathname + url.search;
      }
    };

    let attente = null;
    const graver = () => {
      recopier();
      if (!c.mouvement || c.editeur || !gravure || !laser) return;
      gsap.killTweensOf([gravure, laser]);
      gsap
        .timeline()
        .fromTo(gravure, { clipPath: 'inset(0% 100% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.7, ease: 'none' })
        .fromTo(laser, { left: '14%', opacity: 1 }, { left: '86%', duration: 1.7, ease: 'none' }, 0)
        .to(laser, { opacity: 0, duration: 0.35 });
    };
    const saisir = () => {
      recopier();
      clearTimeout(attente);
      attente = setTimeout(graver, 450);
    };
    [champNoms, champDate].filter(Boolean).forEach((ch) => ch.addEventListener('input', saisir));
    recopier();

    let suivre = null;
    if (c.mouvement && !c.editeur) {
      ScrollTrigger.create({ trigger: el.querySelector('.annetted-essai__scene') || el, start: 'top 70%', once: true, onEnter: graver });
      if (plateau) {
        gsap.set(plateau, { rotationX: 46, rotationZ: -9 });
        gsap.from(plateau, {
          rotationX: 64,
          rotationZ: -18,
          y: 80,
          autoAlpha: 0,
          duration: 1.8,
          ease: 'expo.out',
          scrollTrigger: { trigger: el.querySelector('.annetted-essai__scene') || el, start: 'top 80%', once: true },
        });
      }
      if (c.pointeurFin && plateau) {
        suivre = (e) => plateau.style.setProperty('--lx', `${Math.round((e.clientX / innerWidth) * 100)}%`);
        el.addEventListener('pointermove', suivre);
      }
    }
    return () => {
      [champNoms, champDate].filter(Boolean).forEach((ch) => ch.removeEventListener('input', saisir));
      if (suivre) el.removeEventListener('pointermove', suivre);
      clearTimeout(attente);
    };
  };

  /* ------------------------------------------------------------ duo (deux figures, deux vitesses) */
  SCENES.duo = (el, c) => {
    if (!c.mouvement || c.editeur) return null;
    el.querySelectorAll('[data-vitesse]').forEach((f) => {
      gsap.to(f, {
        yPercent: -18 * parseFloat(f.dataset.vitesse),
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
      });
      const img = f.querySelector('img');
      if (img) {
        gsap.fromTo(img, { scale: 1.3 }, { scale: 1.05, ease: 'none', scrollTrigger: { trigger: f, start: 'top bottom', end: 'bottom top', scrub: true } });
      }
    });
    return null;
  };

  /* ------------------------------------------------------------ atelier */
  SCENES.atelier = (el, c) => {
    if (!c.mouvement || c.editeur) return null;
    const photo = el.querySelector('.annetted-atelier__photo');
    if (photo) {
      gsap.fromTo(
        photo,
        { clipPath: 'inset(16% 22% 16% 22%)' },
        { clipPath: 'inset(0% 0% 0% 0%)', ease: 'none', scrollTrigger: { trigger: photo, start: 'top 92%', end: 'top 18%', scrub: true } }
      );
      const img = photo.querySelector('img');
      if (img) {
        gsap.fromTo(img, { scale: 1.3 }, { scale: 1, ease: 'none', scrollTrigger: { trigger: photo, start: 'top bottom', end: 'bottom top', scrub: true } });
      }
    }
    const lignes = el.querySelectorAll('.annetted-atelier__fiche > div');
    if (lignes.length) {
      gsap.from(lignes, {
        y: 18,
        autoAlpha: 0,
        duration: 0.9,
        ease: 'expo.out',
        stagger: 0.07,
        scrollTrigger: { trigger: el.querySelector('.annetted-atelier__fiche'), start: 'top 85%', once: true },
      });
    }
    return null;
  };

  /* ------------------------------------------------------------ carnet (trois colonnes, trois vitesses) */
  SCENES.carnet = (el, c) => {
    if (!c.mouvement || c.editeur) return null;
    el.querySelectorAll('.annetted-carnet__fig').forEach((fig) => {
      const cadre = fig.querySelector('.annetted-carnet__cadre');
      const img = cadre && cadre.querySelector('img');
      if (!cadre) return;
      const tl = gsap.timeline({ scrollTrigger: { trigger: fig, start: 'top 90%', once: true } });
      tl.fromTo(cadre, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.3, ease: 'expo.out' });
      if (img) tl.fromTo(img, { scale: 1.25 }, { scale: 1, duration: 1.6, ease: 'expo.out' }, 0);
      const legende = fig.querySelector('figcaption');
      if (legende) tl.from(legende, { autoAlpha: 0, y: 10, duration: 0.8, ease: 'expo.out' }, 0.35);
    });
    if (c.bureau) {
      el.querySelectorAll('.annetted-carnet__colonne[data-vitesse]').forEach((col) => {
        gsap.to(col, {
          y: -150 * parseFloat(col.dataset.vitesse),
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
        });
      });
    }
    return null;
  };

  /* ------------------------------------------------------------ journal : aperçu qui suit le curseur */
  SCENES.journal = (el, c) => {
    if (!c.pointeurFin || !c.bureau || !c.mouvement || c.editeur) return null;
    const apercu = document.createElement('div');
    apercu.className = 'annetted-journal-liste__apercu';
    apercu.setAttribute('aria-hidden', 'true');
    const img = document.createElement('img');
    img.alt = '';
    apercu.appendChild(img);
    document.body.appendChild(apercu);
    const ax = gsap.quickTo(apercu, 'x', { duration: 0.8, ease: 'power3.out' });
    const ay = gsap.quickTo(apercu, 'y', { duration: 0.8, ease: 'power3.out' });
    const suivre = (e) => {
      ax(e.clientX);
      ay(e.clientY);
    };
    addEventListener('pointermove', suivre);
    const liens = el.querySelectorAll('[data-apercu]');
    const entrer = (e) => {
      img.src = e.currentTarget.dataset.apercu;
      apercu.classList.add('annetted-journal-liste__apercu--visible');
    };
    const sortir = () => apercu.classList.remove('annetted-journal-liste__apercu--visible');
    liens.forEach((a) => {
      a.addEventListener('pointerenter', entrer);
      a.addEventListener('pointerleave', sortir);
    });
    return () => {
      removeEventListener('pointermove', suivre);
      liens.forEach((a) => {
        a.removeEventListener('pointerenter', entrer);
        a.removeEventListener('pointerleave', sortir);
      });
      apercu.remove();
    };
  };

  /* ------------------------------------------------------------ collection, fiche, À propos */
  // Ces pages ont déjà leurs apparitions (assets/annetted-mouvement.js, classes posées par ce
  // script-là). Ici, seulement ce qui suit le défilement, sur ordinateur. Les images portent déjà
  // une transition CSS sur `transform` (primitive « image ») : on passe donc par les propriétés
  // individuelles `translate` et `scale`, qui n'en dépendent pas.
  const GLISSEES = [
    '.annetted-entete-collection__visuel img',
    '.annetted-apropos__photo img',
    '.annetted-apropos__declaration-media img',
  ].join(', ');

  const pages = (c) => {
    if (!c.bureau || !c.mouvement || c.editeur) return null;
    const nettoyages = [];

    // Les photos glissent lentement dans leur cadre, à contresens du défilement.
    document.querySelectorAll(GLISSEES).forEach((img) => {
      const cadre = img.parentElement;
      img.style.scale = '1.14';
      const declencheur = ScrollTrigger.create({
        trigger: cadre,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: (self) => {
          img.style.translate = `0 ${((self.progress - 0.5) * 9).toFixed(2)}%`;
        },
      });
      nettoyages.push(() => {
        declencheur.kill();
        img.style.scale = '';
        img.style.translate = '';
      });
    });

    // À propos : la grande photo d'ouverture recule et son texte s'efface quand on descend.
    const heros = document.querySelector('.annetted-apropos__heros');
    if (heros) {
      const photo = heros.querySelector('.annetted-apropos__heros-image');
      const contenu = heros.querySelector('.annetted-apropos__heros-contenu');
      const declencheur = ScrollTrigger.create({
        trigger: heros,
        start: 'top top',
        end: 'bottom top',
        onUpdate: (self) => {
          const p = self.progress;
          if (photo) {
            photo.style.scale = (1 + p * 0.12).toFixed(4);
            photo.style.translate = `0 ${(p * 6).toFixed(2)}%`;
          }
          if (contenu) {
            contenu.style.translate = `0 ${(p * -70).toFixed(1)}px`;
            contenu.style.opacity = (1 - Math.min(1, p * 1.5)).toFixed(3);
          }
        },
      });
      nettoyages.push(() => {
        declencheur.kill();
        [photo, contenu].filter(Boolean).forEach((el) => {
          el.style.scale = '';
          el.style.translate = '';
          el.style.opacity = '';
        });
      });
    }

    // À propos : la citation se lit mot à mot, au rythme du défilement.
    const citation = document.querySelector('.annetted-apropos__citation-texte');
    if (citation) {
      c.apresPolices(() => {
        const mots = SplitText.create(citation, { type: 'words', wordsClass: 'annetted-mot' });
        gsap.fromTo(
          mots.words,
          { opacity: 0.3 },
          { opacity: 1, ease: 'none', stagger: 0.4, scrollTrigger: { trigger: citation, start: 'top 80%', end: 'bottom 55%', scrub: true } }
        );
      });
    }

    return () => nettoyages.forEach((f) => f());
  };

  /* ------------------------------------------------------------ communs */
  const titres = (c) => {
    if (!c.mouvement || c.editeur) return;
    c.apresPolices(() => {
      document.querySelectorAll('[data-reveler="lignes"]').forEach((titre) => {
        SplitText.create(titre, {
          type: 'lines',
          mask: 'lines',
          autoSplit: true,
          onSplit: (self) =>
            gsap.from(self.lines, {
              yPercent: 110,
              duration: 1.3,
              ease: 'expo.out',
              stagger: 0.1,
              scrollTrigger: { trigger: titre, start: 'top 85%', once: true },
            }),
        });
      });
    });
  };

  const curseur = (c) => {
    if (!c.pointeurFin || !c.bureau || !c.mouvement || c.editeur) return null;
    const rond = document.createElement('div');
    rond.className = 'annetted-curseur annetted-curseur--absent';
    rond.setAttribute('aria-hidden', 'true');
    rond.innerHTML = '<span class="annetted-curseur__label"></span>';
    document.body.appendChild(rond);
    const label = rond.firstElementChild;
    const cx = gsap.quickTo(rond, 'x', { duration: 0.45, ease: 'power3.out' });
    const cy = gsap.quickTo(rond, 'y', { duration: 0.45, ease: 'power3.out' });
    const bouger = (e) => {
      rond.classList.remove('annetted-curseur--absent');
      cx(e.clientX);
      cy(e.clientY);
      // Les cartes de Horizon et du Journal n'ont pas d'attribut à elles : elles disent « Voir ».
      const cible = e.target instanceof Element ? e.target.closest('[data-curseur], product-card, .annetted-carte-article, .annetted-carte-produit') : null;
      if (cible) {
        label.textContent = cible.dataset.curseur || 'Voir';
        rond.classList.add('annetted-curseur--grand');
      } else {
        rond.classList.remove('annetted-curseur--grand');
      }
    };
    const quitter = () => rond.classList.add('annetted-curseur--absent');
    addEventListener('pointermove', bouger);
    doc.addEventListener('pointerleave', quitter);

    const aimants = Array.from(document.querySelectorAll('[data-aimant]')).map((b) => {
      const bx = gsap.quickTo(b, 'x', { duration: 0.6, ease: 'power3.out' });
      const by = gsap.quickTo(b, 'y', { duration: 0.6, ease: 'power3.out' });
      const tirer = (e) => {
        const r = b.getBoundingClientRect();
        bx((e.clientX - r.left - r.width / 2) * 0.25);
        by((e.clientY - r.top - r.height / 2) * 0.35);
      };
      const lacher = () => {
        bx(0);
        by(0);
      };
      b.addEventListener('pointermove', tirer);
      b.addEventListener('pointerleave', lacher);
      return () => {
        b.removeEventListener('pointermove', tirer);
        b.removeEventListener('pointerleave', lacher);
        gsap.set(b, { x: 0, y: 0 });
      };
    });

    return () => {
      removeEventListener('pointermove', bouger);
      doc.removeEventListener('pointerleave', quitter);
      aimants.forEach((n) => n());
      rond.remove();
    };
  };

  const rideau = (c, apres) => {
    const voile = document.querySelector('.annetted-rideau');
    // Hors de l'accueil il n'y a pas de rideau : ne pas le marquer comme « déjà vu ».
    if (!voile) {
      apres();
      return;
    }
    if (!doc.classList.contains('annetted-rideau-actif') || !c.mouvement || c.editeur) {
      finirRideau();
      apres();
      return;
    }
    Promise.race([policesPretes(), new Promise((r) => setTimeout(r, 800))]).then(() =>
      c.ctx.add(() => {
        const nom = SplitText.create(voile.querySelector('.annetted-rideau__nom'), { type: 'chars', mask: 'chars' });
        const compteur = voile.querySelector('.annetted-rideau__compteur');
        const valeur = { v: 0 };
        gsap
          .timeline({ onComplete: finirRideau })
          .from(nom.chars, { yPercent: 115, duration: 1, ease: 'expo.out', stagger: 0.045 }, 0)
          .to(voile.querySelector('.annetted-rideau__laser'), { scaleX: 1, duration: 1.15, ease: 'power2.inOut' }, 0.2)
          .to(
            valeur,
            {
              v: 100,
              duration: 1.15,
              ease: 'power2.inOut',
              onUpdate: () => {
                if (compteur) compteur.textContent = String(Math.round(valeur.v)).padStart(3, '0');
              },
            },
            0.2
          )
          .to(voile, { clipPath: 'inset(0% 0% 100% 0%)', duration: 1.05, ease: 'expo.inOut' }, '+=0.1')
          .add(apres, '-=0.6');
      })
    );
  };

  /* ------------------------------------------------------------ lancement */
  let mm = null;
  const lancer = () => {
    if (!window.gsap || !window.ScrollTrigger || !window.SplitText) {
      finirRideau();
      return;
    }
    gsap.registerPlugin(ScrollTrigger, SplitText);
    mm = gsap.matchMedia();
    mm.add(
      {
        bureau: '(min-width: 990px)',
        mouvement: '(prefers-reduced-motion: no-preference)',
        pointeurFin: '(pointer: fine)',
      },
      (ctx) => {
        const wrapper = document.querySelector('.page-wrapper');
        const c = {
          ...ctx.conditions,
          editeur: editeur(),
          ctx,
          intros: [],
          scroller: ctx.conditions.bureau && wrapper ? wrapper : window,
        };
        c.apresPolices = (fn) => policesPretes().then(() => ctx.add(fn));
        ScrollTrigger.defaults({ scroller: c.scroller });

        const nettoyages = [];
        let lenis = null;
        if (c.mouvement && c.bureau && c.pointeurFin && !c.editeur && wrapper && window.Lenis) {
          lenis = new window.Lenis({
            wrapper,
            content: wrapper,
            lerp: 0.085,
            smoothWheel: true,
            // Les zones qui défilent seules (tiroirs, recherche, dialogues) gardent leur molette.
            prevent: (node) =>
              Boolean(node.closest && node.closest('dialog, [role="dialog"], [data-lenis-prevent], predictive-search-component, .predictive-search, cart-drawer-component, .menu-drawer')),
          });
          const raf = (t) => lenis.raf(t * 1000);
          lenis.on('scroll', ScrollTrigger.update);
          gsap.ticker.add(raf);
          gsap.ticker.lagSmoothing(0);
          // Les tiroirs de Horizon (panier, menu) posent html[scroll-lock] : Lenis s'arrête avec eux.
          const verrou = new MutationObserver(() => (doc.hasAttribute('scroll-lock') ? lenis.stop() : lenis.start()));
          verrou.observe(doc, { attributes: true, attributeFilter: ['scroll-lock'] });
          nettoyages.push(() => {
            verrou.disconnect();
            gsap.ticker.remove(raf);
            lenis.destroy();
          });
        }
        window.AnnettedAward = { lenis, scroller: c.scroller };

        // Le tiroir du panier rétrécit `.page-wrapper` sans redimensionner la fenêtre : les épingles
        // gardaient leur largeur d'avant. On recalcule quand la largeur du conteneur change.
        if (c.bureau && wrapper && window.ResizeObserver) {
          let largeur = wrapper.clientWidth;
          let attente = 0;
          const veille = new ResizeObserver(() => {
            if (wrapper.clientWidth === largeur) return;
            largeur = wrapper.clientWidth;
            clearTimeout(attente);
            attente = setTimeout(() => {
              if (lenis) lenis.resize();
              ScrollTrigger.refresh();
            }, 260);
          });
          veille.observe(wrapper);
          nettoyages.push(() => {
            clearTimeout(attente);
            veille.disconnect();
          });
        }

        document.querySelectorAll('[data-award]').forEach((el) => {
          const scene = SCENES[el.dataset.award];
          if (!scene) return;
          const n = scene(el, c);
          if (typeof n === 'function') nettoyages.push(n);
        });
        titres(c);
        const n = curseur(c);
        if (n) nettoyages.push(n);
        const p = pages(c);
        if (p) nettoyages.push(p);

        rideau(c, () => ctx.add(() => c.intros.forEach((f) => f())));
        policesPretes().then(() => ScrollTrigger.refresh());

        return () => nettoyages.forEach((f) => f());
      }
    );
  };

  const relancer = () => {
    if (mm) mm.revert();
    lancer();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', lancer);
  else lancer();
  addEventListener('load', () => window.ScrollTrigger && ScrollTrigger.refresh());

  // Éditeur de thème : une section ajoutée, déplacée ou modifiée relance tout.
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((evt) =>
    document.addEventListener(evt, () => {
      clearTimeout(relancer.attente);
      relancer.attente = setTimeout(relancer, 120);
    })
  );
})();
